import { useMemo, ReactNode, ReactElement } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { PageRoutes } from '@/shared/router';
import { StorageKeys } from '@/shared/types';

type Props = {
  element: ReactNode;
};

const splittedPath = location.pathname.split('/');

export const PublicRoute = ({ element }: Props) => {
  const { isLoading, isAuthenticated, error } = useAuth0();
  const location = useLocation();
  const { procId } = useParams();

  const auth0Error = useMemo(() => JSON.parse(error?.message || '{}'), [error]);

  if (isLoading) {
    return null;
  }
  if (
    auth0Error?.code === 'user_not_verified' &&
    location.pathname !== PageRoutes.AUTHORISATION_ERROR
  ) {
    return (
      <Navigate
        to={PageRoutes.AUTHORISATION_ERROR}
        state={{ error: auth0Error }}
        replace
      />
    );
  }
  if (isAuthenticated) {
    // handles invitations (workspace & organization)
    if (localStorage.getItem(StorageKeys.CallbackUrl)) {
      const callbackUrl = localStorage.getItem(StorageKeys.CallbackUrl);
      localStorage.removeItem(StorageKeys.CallbackUrl);
      return <Navigate to={callbackUrl!} state={{ from: location }} replace />;
    }
    // handles proc shareable links
    if (procId && location.pathname === `/procs/${procId}/preview`) {
      return <Navigate to={`/dashboard/proc/${procId}/preview`} replace />;
    }
    // handles public proc instance links
    if (splittedPath.length > 3 && location.pathname.startsWith('/procs/')) {
      return (
        <Navigate
          to={`/dashboard/proc/instance/${
            splittedPath[splittedPath.length - 2]
          }`}
          replace
        />
      );
    }

    // handles organization discover page links
    const lastPathPart = document.location.href.substring(
      document.location.href.lastIndexOf('/') + 1,
    );

    if (
      document.location.href.includes(
        `${import.meta.env.VITE_BASE_URL}/discover/`,
      ) &&
      !lastPathPart.includes('discover')
    ) {
      return <Navigate to={`/dashboard/discover/${lastPathPart}`} />;
    }

    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }
  return element as ReactElement;
};
